import type { WaterFrom } from "@aybuben/client/analytics/events.ts";
import type { EventProps } from "@aybuben/client/analytics/events.ts";
import {
	abandonProps,
	completionProps,
	missProps,
	momentProps,
	type LessonTag,
} from "@aybuben/client/analytics/lesson.ts";
import type { SignInMethod } from "@aybuben/client/auth/flow.ts";
import type { AuthSource, AuthUser } from "@aybuben/client/auth/source.ts";
import { isListeningOff, LISTENING_OFF_MS, minutesLeft } from "@aybuben/client/device/listening.ts";
import {
	localPolaroidMemory,
	type PolaroidMemory,
	type PolaroidMemoryStore,
} from "@aybuben/client/device/polaroid-memory.ts";
import type { DevicePrefs } from "@aybuben/client/device/prefs.ts";
import { loadTheme } from "@aybuben/client/device/theme-choice.ts";
import {
	EMPTY_TREE_MEMORY,
	gained,
	localTreeMemory,
	weekStart,
	worthAWeek,
	type TreeMemory,
	type TreeMemoryStore,
} from "@aybuben/client/device/tree-memory.ts";
import { localWritingStore, type WritingStore } from "@aybuben/client/device/writing.ts";
import {
	canMuteRound,
	closesRound,
	gateEvent,
	gateFor,
	mutedSummary,
	pausedProps,
	settleDone,
	type GateReason,
	type ListeningPause,
} from "@aybuben/client/lesson/listening-gate.ts";
import type { LessonDone } from "@aybuben/client/lesson/runner.ts";
import { hasComposed } from "@aybuben/client/moments/moments.ts";
import { DUR } from "@aybuben/client/motion.ts";
import { lessonAudio, stepsAudio } from "@aybuben/client/offline/audio-sets.ts";
import { createOfflineAudio, type OfflineAudio } from "@aybuben/client/offline/controller.ts";
import { DownloadError } from "@aybuben/client/offline/types.ts";
import { reconcile } from "@aybuben/client/onboarding/reconcile.ts";
import { takenAt } from "@aybuben/client/places/moment.ts";
import { atFinish, catchUp, revealStep, sameMemory } from "@aybuben/client/places/moments.ts";
import { photoDate } from "@aybuben/client/places/photo.ts";
import { stopState, stopsOf, type Stop } from "@aybuben/client/places/stops.ts";
import type { ProgressStore } from "@aybuben/client/progress/store.ts";
import type { SyncStatus } from "@aybuben/client/progress/store.ts";
import { placeOf, type FocusRequest } from "@aybuben/client/screens/home.ts";
import {
	lessonStep,
	resultModel,
	treeStepNeeded,
	type LessonStep1,
} from "@aybuben/client/screens/result.ts";
import { seedFor } from "@aybuben/client/tree/seeds.ts";
import { shareText } from "@aybuben/client/tree/share-card.ts";
import { BundledAssetSource, type AssetSource } from "@aybuben/core/content/assets.ts";
import { unitOf, type UnitView, type LetterView } from "@aybuben/core/content/course.ts";
import type { LoadedContent } from "@aybuben/core/content/source.ts";
import type { LessonId, LessonDef, ProgressKey } from "@aybuben/core/content/types.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import { buildKeyReview, buildLesson, buildPractice } from "@aybuben/core/lesson.ts";
import type { LessonStep } from "@aybuben/core/lesson.ts";
import { ONBOARDING_VERSION } from "@aybuben/core/onboarding/version.ts";
import type { PracticeMode } from "@aybuben/core/practice.ts";
import { makeEvent, type EventBody, type LessonResult } from "@aybuben/core/progress/event.ts";
import { guestWall, savePrompt } from "@aybuben/core/progress/guest.ts";
import { startedIn } from "@aybuben/core/progress/query.ts";
import { summarise, type LessonSummary } from "@aybuben/core/progress/reduce.ts";
import {
	treeMoment,
	treeState,
	waterRound,
	type TreeMoment,
	type TreeState,
} from "@aybuben/core/progress/tree.ts";
import { uid } from "@aybuben/core/rng.ts";
import {
	lessonSilencer,
	reviewSilencer,
	silence as silenceSteps,
	type Silencer,
} from "@aybuben/core/silent.ts";
import type { GameState } from "@aybuben/core/state.ts";
import {
	createContext,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useStore } from "zustand";

import { BROWSER_PLAYER_ENV, CachingSoundPlayer } from "./audio-cache.ts";
import { armCueUnlock, play } from "./audio.ts";
import { capture } from "./capture.ts";
import { CourseProvider } from "./content/context.tsx";
import { saveTheme } from "./device/theme.ts";
import { haptic } from "./haptics.ts";
import { StringsProvider, translator, useStrings } from "./i18n/index.tsx";
import { setBusy } from "./stale-build.ts";
import { COL } from "./theme.ts";
import { AccountBody, LanguagePicker, LayoutPicker, ThemePicker } from "./ui/Account.tsx";
import { Home } from "./ui/Home.tsx";
import { PICTURE } from "./ui/home/picture-size.ts";
import { lazyPart, PartBoundary, PartFailed, preloadWhenIdle, usePartError } from "./ui/lazy.tsx";
import { Lesson } from "./ui/Lesson.tsx";
import { LessonDone as LessonDoneStep } from "./ui/LessonDone.tsx";
import { LessonSheet } from "./ui/LessonSheet.tsx";
import { LettersView, LetterSheet } from "./ui/Letters.tsx";
import { useListening } from "./ui/listening/context.tsx";
import { Gate } from "./ui/listening/Gate.tsx";
import { ListeningScope } from "./ui/listening/Scope.tsx";
import { Onboarding } from "./ui/onboarding/Onboarding.tsx";
import { cardArtPart, polaroidPart } from "./ui/places/art.tsx";
import { Practice } from "./ui/Practice.tsx";
import {
	Header,
	Nav,
	Shell,
	Sheet,
	Btn,
	G,
	Splash,
	TAB_PATH,
	tabOf,
	type TabId,
} from "./ui/primitives.tsx";
import { polaroidMoment, Result } from "./ui/Result.tsx";
import { SaveProgressSheet } from "./ui/SaveProgressSheet.tsx";
import { SoundProvider } from "./ui/sound.tsx";
import { sharePolaroid, shareTree } from "./ui/tree/share.ts";
import { WeekSheet } from "./ui/WeekSheet.tsx";
import { WritingProvider } from "./ui/writing/context.tsx";

/** No reveal playing: one set, so clearing an already clear reveal renders nothing. */
const NOTHING: ReadonlySet<string> = new Set();

/** For mounts without a controller (tests): online, nothing stored, nothing gated. */
const NO_OFFLINE_AUDIO: OfflineAudio = createOfflineAudio({
	store: null,
	download: () => Promise.reject(new DownloadError("network", "no store")),
	isOnline: () => true,
	now: () => 0,
});

/** A lesson in progress. `unitId` is null for a practice round and a review of given keys. */
interface ActiveLesson {
	unitId: string | null;
	/** Null for a practice round or a review of given keys, which belong to no unit and so to no lesson. */
	lessonId: string | null;
	/** The practice mode, or null for a unit's lesson and for a review of given keys. */
	mode: PracticeMode | null;
	steps: LessonStep[];
	key: string;
	/** Silences the rest of the round on "Can't listen now"; absent when the lesson teaches by sound. */
	silence?: Silencer;
	/** Set when the round was built with listening off: what that changed. */
	muted?: LessonResult["muted"];
	/** The round needs sound (`closesRound`): "Can't listen now" closes it onto the gate, which reopens it with this. */
	reopen?: () => void;
}

/** A round a muted learner opened but needs sound: what the gate says and what it opens. */
interface ListeningGate {
	reason: GateReason;
	minutesLeft: number;
	/** What `listening_gate_shown` says, before the choice. */
	event: Omit<EventProps["listening_gate_shown"], "choice">;
	/** Opens the round with sound, after the learner turned listening back on. */
	open: () => void;
}

/** A finished lesson: its summary and what it did to the tree. */
interface Finished {
	summary: LessonSummary;
	moment: TreeMoment;
	/** The stops this finish animates, in course order: their Polaroid moments come first. */
	polaroids: string[];
	/** What the lesson step shows: from the runner's stats, so it is computed once, here. */
	lessonStep: LessonStep1;
	/** Whether the tree changed enough to earn its own step. */
	treeNeeded: boolean;
	/** The round that produced it: the lesson step's analytics events name it. */
	tag: LessonTag;
}

/** Which part of a lesson's ending is on screen. */
type EndStep = "lesson" | "moments" | "tree";

/** The lesson whose sheet is open. */
interface SheetTarget {
	u: UnitView;
	lesson: LessonDef;
}

interface AppLayoutProps {
	content: LoadedContent;
	store: ProgressStore;
	email: string;
	guest: boolean;
	device: DevicePrefs;
	onDevice: (patch: Partial<DevicePrefs>) => void;
	/**
	 * The account's whole remote log is folded in. Onboarding is reconciled only
	 * then — against an unsynced account a tour could repeat and a language be
	 * overwritten — except for a guest created just now, who has no remote log.
	 */
	pulled: boolean;
	/** A guest was just created: open lesson one instead of the map. */
	firstLesson: boolean;
	onFirstLesson: () => void;
	auth: AuthSource;
	onLinked: (u: AuthUser, method: SignInMethod) => void;
	onLeaveGuest: (email: string | null) => Promise<void>;
	/** The save sheet is on its code step, where the guest session may be swapped away. */
	onSaving: (saving: boolean) => void;
	onSignOut: () => Promise<void>;
	onDelete: () => Promise<void>;
	/** Recording readiness: Root's controller. */
	offlineAudio?: OfflineAudio;
	/** Tiles or keyboard on this device. */
	writingStore?: WritingStore;
	/** The signed-in account: picks the tree's shape and keys what this device remembers of it. */
	userId?: string;
	/** What this device remembers of the tree. */
	treeMemoryStore?: TreeMemoryStore;
	/** What this device remembers of its Polaroids. */
	polaroidMemoryStore?: PolaroidMemoryStore;
	/** Plays a cue (tests spy on it). */
	cue?: typeof play;
}

// Context for page components to access AppLayout state and callbacks
interface AppLayoutContextValue {
	st: GameState;
	guest: boolean;
	openSave: () => void;
	onLeaveGuest: (email: string | null) => Promise<void>;
	sync: SyncStatus;
	tab: TabId;
	setTab: (t: TabId) => void;
	lessonSheet: SheetTarget | null;
	setLessonSheet: (s: SheetTarget | null) => void;
	letterSheet: LetterView | null;
	setLetterSheet: (l: LetterView | null) => void;
	lesson: ActiveLesson | null;
	setLesson: (l: ActiveLesson | null) => void;
	result: Finished | null;
	setResult: (r: Finished | null) => void;
	treeOpen: boolean;
	onOpenTree: () => void;
	onCloseTree: () => void;
	/** The learner's tree shape. */
	seed: number;
	treeMemory: TreeMemory;
	/** The tree the sheet grows from: what the learner last saw, when it has gained since. */
	treeFrom: TreeState | undefined;
	/** Bumped as a result closes, for the header's bounce. */
	arrive: number;
	/** Starts the shortest round that brings summer back. */
	onWater: (from: WaterFrom) => void;
	onRenameTree: (name: string) => void;
	onShareTree: (scene: HTMLElement) => void;
	/** Null until loaded, and after a load that found none. */
	polaroids: PolaroidMemory | null;
	/** Nothing reads it to write, and nothing writes it, before this. */
	polaroidsLoaded: boolean;
	/** The memory as last written: a writer builds on it, so two writes never lose each other's entries. */
	polaroidsRef: { current: PolaroidMemory | null };
	/** A no-op before the load; saves only a change. */
	rememberPolaroids: (next: PolaroidMemory) => void;
	/** The stop whose Polaroid card is open. */
	photo: string | null;
	openPhoto: (stopId: string) => void;
	closePhoto: () => void;
	/** The stops whose place card plays its reveal now. */
	revealing: ReadonlySet<string>;
	onOpenPlace: (stopId: string) => void;
	/** The stop whose place sheet is open. */
	placeSheet: string | null;
	setPlaceSheet: (stopId: string | null) => void;
	stops: Stop[];
	assetSource: AssetSource;
	player: CachingSoundPlayer;
	record: (body: EventBody) => void;
	switchLang: (next: LangId) => void;
	startLesson: (target: SheetTarget) => void;
	lessonOffline: (u: UnitView, l: LessonDef) => boolean;
	startPractice: (mode: PracticeMode) => void;
	onReviewKeys: (keys: ProgressKey[]) => void;
	finish: (res: LessonDone) => void;
	/** A pending request for Home to scroll to the learner's place, or null. */
	focus: FocusRequest | null;
	onFocused: () => void;
	// Callbacks for page components
	onLesson: (u: UnitView, lesson: LessonDef) => void;
	onPick: (l: LetterView) => void;
	onContinue: () => void;
	onCloseLessonSheet: () => void;
	onStartLesson: () => void;
	onCloseLetterSheet: () => void;
	email: string;
	onSignOut: () => Promise<void>;
	onDelete: () => Promise<void>;
}

const AppLayoutContext = createContext<AppLayoutContextValue | null>(null);

export function useAppLayout(): AppLayoutContextValue {
	const ctx = useContext(AppLayoutContext);
	if (!ctx) throw new Error("useAppLayout called outside AppLayoutContext");
	return ctx;
}

/** The header every page carries, with the window wired to this learner's Home. */
function AppHeader() {
	const { st, onOpenTree, seed, treeMemory, arrive } = useAppLayout();
	return (
		<Header st={st} onOpenTree={onOpenTree} seed={seed} seen={treeMemory.seen} arrive={arrive} />
	);
}

// Page components - each consumes the context. None mounts a CourseProvider:
// AppLayout puts one above the pages and the sheets together, and a second
// nested one would only invite the two sources to drift apart.

export function HomePage() {
	const {
		st,
		onLesson,
		onReviewKeys,
		setTab,
		focus,
		onFocused,
		lessonOffline,
		onOpenPlace,
		openPhoto,
		polaroids,
		revealing,
	} = useAppLayout();

	return (
		<Shell>
			<AppHeader />
			<Home
				st={st}
				onLesson={onLesson}
				focus={focus}
				onFocused={onFocused}
				onReviewKeys={onReviewKeys}
				isOffline={lessonOffline}
				onOpenPlace={onOpenPlace}
				onOpenPhoto={openPhoto}
				polaroids={polaroids}
				revealing={revealing}
				onJump={(direction) => capture("path_jumped", { direction })}
			/>
			<Nav tab="learn" setTab={setTab} />
		</Shell>
	);
}

export function LettersPage() {
	const { st, onPick, setTab } = useAppLayout();

	return (
		<Shell>
			<AppHeader />
			<LettersView st={st} onPick={onPick} />
			<Nav tab="letters" setTab={setTab} />
		</Shell>
	);
}

export function PracticePage() {
	const { st, setTab, startPractice, onWater, seed } = useAppLayout();

	return (
		<Shell>
			<AppHeader />
			<Practice st={st} onStart={startPractice} onWater={() => onWater("practice")} seed={seed} />
			<Nav tab="practice" setTab={setTab} />
		</Shell>
	);
}

export function AccountPage() {
	const {
		st,
		sync,
		email,
		guest,
		openSave,
		onLeaveGuest,
		onSignOut,
		onDelete,
		switchLang,
		record,
		setTab,
	} = useAppLayout();
	const t = useStrings();
	const [theme, setTheme] = useState(loadTheme);

	return (
		<Shell>
			<AppHeader />
			<div className="px-4 pt-4 pb-32">
				<h1 className="text-2xl font-extrabold">{t("account.title")}</h1>
				<AccountBody
					email={email}
					sync={sync}
					onSignOut={onSignOut}
					onDelete={onDelete}
					guest={guest}
					onSave={openSave}
					onLeaveGuest={() => onLeaveGuest(null)}
				>
					<div className="mt-6">
						<div className="text-sm font-semibold" style={{ color: COL.mute }}>
							{t("account.language")}
						</div>
						<LanguagePicker lang={st.lang} onPick={switchLang} />
					</div>
					<div className="mt-6">
						<div className="text-sm font-semibold" style={{ color: COL.mute }}>
							{t("account.keyboard")}
						</div>
						<LayoutPicker
							layout={st.layout}
							onPick={(layout) => {
								if (layout !== st.layout) record({ kind: "pref", patch: { layout } });
							}}
						/>
						<p className="mt-2 text-xs leading-snug" style={{ color: COL.mute }}>
							{t("account.keyboardNote")}
						</p>
					</div>
					<div className="mt-6">
						<div className="text-sm font-semibold" style={{ color: COL.mute }}>
							{t("account.theme")}
						</div>
						<ThemePicker
							theme={theme}
							onPick={(next) => {
								saveTheme(next);
								setTheme(next);
							}}
						/>
					</div>
				</AccountBody>
			</div>
			<Nav tab="account" setTab={setTab} />
		</Shell>
	);
}

// Sheets and overlay components (not full pages)
function LessonSheetOverlay() {
	const { lessonSheet, st, onCloseLessonSheet, onStartLesson, lessonOffline } = useAppLayout();
	if (!lessonSheet) return null;
	return (
		<LessonSheet
			u={lessonSheet.u}
			lesson={lessonSheet.lesson}
			st={st}
			onClose={onCloseLessonSheet}
			onStart={onStartLesson}
			offline={lessonOffline(lessonSheet.u, lessonSheet.lesson)}
		/>
	);
}

function OfflineNotice({ onClose }: { onClose: () => void }) {
	const t = useStrings();
	return (
		<Sheet onClose={onClose}>
			<div className="text-2xl leading-tight font-extrabold">{t("offline.title")}</div>
			<p className="mt-3 text-sm leading-relaxed" style={{ color: COL.mute }}>
				{t("offline.body")}
			</p>
			<div className="mt-5">
				<Btn onClick={onClose} color={COL.pom}>
					{t("common.close")}
				</Btn>
			</div>
		</Sheet>
	);
}

function LetterSheetOverlay() {
	const { letterSheet, onCloseLetterSheet } = useAppLayout();
	if (!letterSheet) return null;
	return <LetterSheet L={letterSheet} onClose={onCloseLetterSheet} />;
}

// Lazy: the Home picture and the Polaroids draw the world art, ~50 KiB gzip the startup bundle cannot carry.
const homeSheet = lazyPart(() => import("./ui/home/HomeSheet.tsx").then((m) => m.HomeSheet));
const polaroidCard = lazyPart(() =>
	import("./ui/home/PolaroidCard.tsx").then((m) => m.PolaroidCard),
);
const placeSheetPart = lazyPart(() =>
	import("./ui/places/PlaceSheet.tsx").then((m) => m.PlaceSheet),
);
const HomeSheet = homeSheet.Part;
const PolaroidCard = polaroidCard.Part;
const PlaceSheet = placeSheetPart.Part;

/** The sheet's frame and picture box while its chunk arrives: the sheet opens at once, the art fills in. */
const HomeSheetFrame = ({ onClose }: { onClose: () => void }) => (
	<Sheet bleed onClose={onClose}>
		<div style={{ aspectRatio: `${PICTURE.w} / ${PICTURE.h}`, background: COL.bg }} />
	</Sheet>
);

function HomeSheetOverlay() {
	const {
		st,
		onCloseTree,
		seed,
		treeFrom,
		onWater,
		onRenameTree,
		onShareTree,
		polaroids,
		polaroidsLoaded,
		polaroidsRef,
		rememberPolaroids,
		stops,
		openPhoto,
	} = useAppLayout();
	// Stops finished elsewhere (a sync) join the memory silently: their photos hang undated, no moment.
	// Not keyed on the callbacks: they are rebuilt with every layout render.
	useEffect(() => {
		if (polaroidsLoaded) rememberPolaroids(catchUp(polaroidsRef.current, st, stops));
	}, [polaroidsLoaded, st, stops]);
	const onPartError = usePartError("home-sheet");
	return (
		<PartBoundary fallback={<PartFailed onClose={onCloseTree} />} onError={onPartError}>
			<Suspense fallback={<HomeSheetFrame onClose={onCloseTree} />}>
				<HomeSheet
					st={st}
					seed={seed}
					from={treeFrom}
					memory={polaroidsLoaded ? polaroids : undefined}
					onOpenPhoto={openPhoto}
					onClose={onCloseTree}
					onWater={() => {
						onCloseTree();
						onWater("sheet");
					}}
					onRename={onRenameTree}
					onShare={onShareTree}
				/>
			</Suspense>
		</PartBoundary>
	);
}

function PlaceSheetOverlay({ onUnit }: { onUnit: (unitId: string) => void }) {
	const { st, stops, placeSheet: id, setPlaceSheet, polaroids, openPhoto } = useAppLayout();
	const onPartError = usePartError("place-sheet");
	// The moment the sheet opened: render must stay pure, and the sheet is short-lived.
	const [now] = useState(Date.now);
	const season = useMemo(() => treeState(st, now).season, [st, now]);
	const stop = stops.find((s) => s.id === id);
	const close = () => setPlaceSheet(null);
	// A stop that left the course (a release change under an open sheet): nothing to show.
	useEffect(() => {
		if (!stop) close();
	}, [stop]);
	if (!stop) return null;
	return (
		<PartBoundary fallback={<PartFailed onClose={close} />} onError={onPartError}>
			{/* The same picture box as the Home sheet's: the sheet opens at once, the art fills in. */}
			<Suspense fallback={<HomeSheetFrame onClose={close} />}>
				<PlaceSheet
					stop={stop}
					st={st}
					taken={polaroids?.taken[stop.id] ?? null}
					lang={st.lang}
					season={season}
					onClose={close}
					onUnit={(unitId) => {
						close();
						onUnit(unitId);
					}}
					onOpenPhoto={openPhoto}
				/>
			</Suspense>
		</PartBoundary>
	);
}

/** A sibling of the Home sheet, not inside it: a click in the sheet's subtree would reach its backdrop and close Home. */
function PolaroidCardOverlay() {
	const { st, stops, photo, polaroids, closePhoto } = useAppLayout();
	const onPartError = usePartError("polaroid-card");
	const stop = stops.find((s) => s.id === photo);
	// A photo whose stop left the course (a release change under an open card): nothing to show.
	useEffect(() => {
		if (!stop) closePhoto();
	}, [stop]);
	if (!stop) return null;
	const taken = polaroids?.taken[stop.id] ?? null;
	const t = translator(st.lang);
	return (
		<PartBoundary fallback={<PartFailed onClose={closePhoto} />} onError={onPartError}>
			{/* Nothing while it loads: the card is a modal over whatever is open, which stays put. */}
			<Suspense fallback={null}>
				<PolaroidCard
					stop={stop}
					taken={taken}
					lang={st.lang}
					onClose={closePhoto}
					onShare={(el) => {
						void sharePolaroid(el, {
							hy: stop.place.hy,
							name: stop.place.name,
							date: photoDate(taken, st.lang),
							line: t("polaroid.shareLine"),
							text: t("polaroid.shareText", { name: stop.place.name }),
						}).then((outcome) => {
							capture("polaroid_shared", { place: stop.id, from: "card", outcome });
						});
					}}
				/>
			</Suspense>
		</PartBoundary>
	);
}

function Overlays({
	week,
	onPlaceUnit,
}: {
	week: ReactNode;
	onPlaceUnit: (unitId: string) => void;
}) {
	const { lessonSheet, letterSheet, treeOpen, photo, placeSheet } = useAppLayout();
	return (
		<>
			{lessonSheet && <LessonSheetOverlay />}
			{letterSheet && <LetterSheetOverlay />}
			{treeOpen && <HomeSheetOverlay />}
			{placeSheet && <PlaceSheetOverlay onUnit={onPlaceUnit} />}
			{/* After the place sheet: the card it opens comes up above it. */}
			{photo && <PolaroidCardOverlay />}
			{!lessonSheet && !letterSheet && !treeOpen && !placeSheet && week}
		</>
	);
}

export default function AppLayout({
	content,
	store,
	email,
	guest,
	device,
	onDevice,
	pulled,
	firstLesson,
	onFirstLesson,
	auth,
	onLinked,
	onLeaveGuest,
	onSaving,
	onSignOut,
	onDelete,
	offlineAudio = NO_OFFLINE_AUDIO,
	writingStore = localWritingStore,
	userId = "",
	treeMemoryStore = localTreeMemory,
	polaroidMemoryStore = localPolaroidMemory,
	cue = play,
}: AppLayoutProps) {
	const st = useStore(store, (s) => s.state);
	const sync = useStore(store, (s) => s.sync);
	const record = useCallback(
		(body: EventBody): void => {
			const s = store.getState();
			s.record(makeEvent(body, s.ctx));
		},
		[store],
	);
	const location = useLocation();
	const navigate = useNavigate();
	const course = useMemo(() => content.course(st.lang), [content, st.lang]);
	const tab: TabId = tabOf(location.pathname);
	// Asks Home to scroll to the learner's place. A counter, not a flag: Home
	// unmounts for a lesson, a result and the other tabs, so "Home just mounted"
	// cannot tell a page load or a finished lesson from a plain tab switch, which
	// must keep the learner's place instead. The first request is the page load's.
	const [focus, setFocus] = useState<FocusRequest>({ n: 1, smooth: false });
	const [focusHandled, setFocusHandled] = useState(0);
	const requestFocus = (smooth: boolean, unitId?: string) =>
		setFocus((f) => (unitId ? { n: f.n + 1, smooth, unitId } : { n: f.n + 1, smooth }));
	const pendingFocus = focus.n === focusHandled ? null : focus;

	// Whether the learner has done anything since mount: switched tabs, started
	// a lesson, or touched, scrolled or typed at all.
	const moved = useRef(false);
	useEffect(() => {
		const stir = () => {
			moved.current = true;
		};
		const events = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
		for (const e of events) window.addEventListener(e, stir, { once: true, passive: true });
		return () => {
			for (const e of events) window.removeEventListener(e, stir);
		};
	}, []);
	// A new device folds the account's history after mount, and the learner's
	// place moves with it: aim again, once. Only when the place actually moved —
	// every page load ends its first pull, and an unchanged place needs no jump —
	// and only if the learner has not started using the page meanwhile.
	const [placeAtMount] = useState(() => placeOf(st, course.units));
	const sawUnpulled = useRef(!pulled);
	useEffect(() => {
		if (!pulled || !sawUnpulled.current) return;
		sawUnpulled.current = false;
		// Read the store, not `st`: this render may predate the pulled fold's.
		if (!moved.current && placeOf(store.getState().state, course.units) !== placeAtMount)
			requestFocus(false);
	}, [pulled]);
	// Each tab keeps its own place. The tabs share one window scroll, so without
	// this the shorter Letters page clamps it and Learn comes back somewhere else.
	const tabScroll = useRef<Record<TabId, number>>({
		learn: 0,
		letters: 0,
		practice: 0,
		account: 0,
	});
	// Learn also owns addresses that are not the map: their scroll is not Learn's.
	const saveTabScroll = () => {
		if (location.pathname === TAB_PATH[tab]) tabScroll.current[tab] = window.scrollY;
	};
	const setTab = (t: TabId) => {
		moved.current = true;
		// Re-tapping Learn is how a learner asks for their place in the course.
		if (t === tab) {
			if (t === "learn") requestFocus(true);
			return;
		}
		saveTabScroll();
		navigate(TAB_PATH[t]);
	};
	// Layout, not passive: restore before paint so the old position never flashes.
	// Home's focus scroll is a passive effect, so a pending request still wins.
	// Keyed on the address, not the tab: Learn owns addresses that are not the
	// map, and a tab key would carry the map's scroll onto them.
	useLayoutEffect(() => {
		window.scrollTo(0, location.pathname === TAB_PATH[tab] ? tabScroll.current[tab] : 0);
	}, [location.pathname]);
	const [lessonSheet, setLessonSheet] = useState<SheetTarget | null>(null);
	const [letterSheet, setLetterSheet] = useState<LetterView | null>(null);
	const [lesson, setLesson] = useState<ActiveLesson | null>(null);
	const listening = useListening();
	// Read by the handlers that start a round, which must see the window as it is at the tap.
	const offRef = useRef(listening.off);
	useEffect(() => {
		offRef.current = listening.off;
	}, [listening.off]);
	const [gate, setGate] = useState<ListeningGate | null>(null);
	// What a tap on "Can't listen now" changed in the running round, for an abandonment to report.
	const tapped = useRef<LessonResult["muted"]>(undefined);
	const [result, setResult] = useState<Finished | null>(null);
	// Beside `result`, not in it: a step change must not make it a "new" result for the save prompt.
	const [endStep, setEndStep] = useState<EndStep>("lesson");
	const [treeOpen, setTreeOpen] = useState(false);
	const seed = useMemo(() => seedFor(userId), [userId]);
	const [treeMemory, setTreeMemory] = useState<TreeMemory>(EMPTY_TREE_MEMORY);
	const [treeMemoryLoaded, setTreeMemoryLoaded] = useState(false);
	useEffect(() => {
		let live = true;
		void treeMemoryStore.load(userId).then((m) => {
			if (!live) return;
			setTreeMemory(m);
			setTreeMemoryLoaded(true);
		});
		return () => {
			live = false;
		};
	}, [treeMemoryStore, userId]);
	const rememberTree = (patch: Partial<TreeMemory>) =>
		setTreeMemory((m) => {
			const next = { ...m, ...patch };
			void treeMemoryStore.save(userId, next);
			return next;
		});
	const [loadedPolaroids, setPolaroids] = useState<PolaroidMemory | null>(null);
	/** Whose memory `loadedPolaroids` is: on a switch, the new learner's first render still holds the last one's. */
	const [polaroidsFor, setPolaroidsFor] = useState<string | null>(null);
	const polaroidsLoaded = polaroidsFor === userId;
	const polaroids = polaroidsLoaded ? loadedPolaroids : null;
	const polaroidsRef = useRef<PolaroidMemory | null>(null);
	// A ref too: a write's closure may outlive the render that made it.
	const loadedForRef = useRef<string | null>(null);
	useEffect(() => {
		let live = true;
		// Another learner's memory must not show, or be written over, while this one loads.
		loadedForRef.current = null;
		polaroidsRef.current = null;
		setPolaroids(null);
		setPolaroidsFor(null);
		void polaroidMemoryStore.load(userId).then((m) => {
			if (!live) return;
			polaroidsRef.current = m;
			loadedForRef.current = userId;
			setPolaroids(m);
			setPolaroidsFor(userId);
		});
		return () => {
			live = false;
		};
	}, [polaroidMemoryStore, userId]);
	/**
	 * Every write goes through here. Until this learner's load resolves it is a no-op: a write then would
	 * replace the stored dates, or land the last learner's memory under this one's key.
	 */
	const rememberPolaroids = (next: PolaroidMemory) => {
		if (loadedForRef.current !== userId) return;
		if (sameMemory(polaroidsRef.current, next)) return;
		polaroidsRef.current = next;
		setPolaroids(next);
		void polaroidMemoryStore.save(userId, next);
	};
	const stops = useMemo(() => stopsOf(course.views.units()), [course]);
	// Fetched once the shell has settled, so the first tap on the home window opens at once.
	// The place parts only when the course has stops: content without places fetches nothing new.
	const hasStops = stops.length > 0;
	useEffect(
		() =>
			preloadWhenIdle(
				homeSheet,
				polaroidCard,
				polaroidMoment,
				...(hasStops ? [cardArtPart, polaroidPart, placeSheetPart] : []),
			),
		[hasStops],
	);
	const [photo, setPhoto] = useState<string | null>(null);
	const [placeSheet, setPlaceSheet] = useState<string | null>(null);
	// The reveal plays on the Path itself: not under a lesson or its result, whose moment comes first.
	const onLearn = location.pathname === TAB_PATH.learn && !lesson && !result;
	const [revealing, setRevealing] = useState<ReadonlySet<string>>(NOTHING);
	// Before the reveal below, in the same layout pass: arriving on the Path drops a reveal
	// left from an earlier visit (its card would replay the animation on remount), and a
	// passive effect here would run after the reveal and wipe the new one too.
	useLayoutEffect(() => setRevealing(NOTHING), [location.pathname]);
	// Before paint: after a finish the next card is already "current", in
	// colour, and must not flash it before the reveal greys it back.
	// Not keyed on rememberPolaroids or cue: rebuilt with every render, and the
	// ref guard inside rememberPolaroids keeps a write from landing early.
	useLayoutEffect(() => {
		if (!onLearn || !polaroidsLoaded) return;
		const { memory, reveal } = revealStep(polaroidsRef.current, st, stops);
		rememberPolaroids(memory);
		if (!reveal.length) return;
		setRevealing(new Set(reveal));
		cue("unveil");
		haptic("reveal");
	}, [onLearn, polaroidsLoaded, st, stops]);
	// The 900 ms clock follows the reveal itself, not `st`: a layout-effect cleanup
	// on an `st` change would cancel it and the re-run, owing nothing, would not re-arm it.
	useEffect(() => {
		if (!revealing.size) return;
		const id = setTimeout(() => setRevealing(NOTHING), DUR.reveal);
		return () => clearTimeout(id);
	}, [revealing]);
	const [treeFrom, setTreeFrom] = useState<TreeState | undefined>(undefined);
	const [arrive, setArrive] = useState(0);
	const [celebrating, setCelebrating] = useState(false);
	/** The result's Polaroid moments not yet pinned. */
	const [momentsLeft, setMomentsLeft] = useState(0);
	const [week, setWeek] = useState<{ from: TreeState; to: TreeState; since: number } | null>(null);

	// What the in-app keyboard leaves undimmed: the letters of every unit
	// started, the same "met" the Letters tab counts. Keyed on `done` alone,
	// so a pref or sound toggle keeps the same set and the writing context.
	const done = st.done;
	const knownLetters = useMemo(
		() =>
			new Set(
				course.views
					.letters()
					.filter((L) => startedIn(done, unitOf(L.id)))
					.map((L) => L.l),
			),
		[course, done],
	);
	const onHinted = useCallback(
		() => record({ kind: "pref", patch: { kbdHinted: true } }),
		[record],
	);
	const assetSource = content.assets;
	const player = useMemo(
		() =>
			new CachingSoundPlayer(assetSource, undefined, {
				...BROWSER_PLAYER_ENV,
				// A recording that downloads proves the connection is back, sooner than any event.
				fetch: (url) =>
					BROWSER_PLAYER_ENV.fetch(url).then((r) => {
						if (r.ok) offlineAudio.setOnline(true);
						return r;
					}),
			}),
		[assetSource, offlineAudio],
	);
	const offline = useStore(offlineAudio.state, (s) => s.offline);
	// Readiness gates nothing online, so only offline do landed recordings re-render the app.
	const storedKeys = useStore(offlineAudio.state, (s) => (s.offline ? s.storedKeys : null));
	// Built only while offline. Each lesson's pools are walked once per course
	// (`lessonAudio` caches), so going offline again is cheap.
	const lessonSets = useMemo(() => {
		if (!offline) return null;
		const m = new Map<string, ReadonlySet<string>>();
		for (const u of course.units) for (const l of u.lessons) m.set(l.id, lessonAudio(course, u, l));
		return m;
	}, [course, offline]);
	const blockedLessons = useMemo(() => {
		if (!lessonSets || !storedKeys) return null;
		const out = new Set<string>();
		for (const [id, ids] of lessonSets) if (!offlineAudio.stored(ids, assetSource)) out.add(id);
		return out;
	}, [lessonSets, storedKeys, offlineAudio, assetSource]);
	/** Whether the lesson, played with sound, lacks a recording offline. */
	const lessonLacksSound = (u: UnitView, l: LessonDef): boolean =>
		u.def.lessons.includes(l) && (blockedLessons?.has(l.id) ?? false);
	// A muted lesson plays none of the recordings its sheet counts, so it never waits for them.
	const lessonOffline = (u: UnitView, l: LessonDef): boolean =>
		listening.off === null && lessonLacksSound(u, l);
	const [offlineNotice, setOfflineNotice] = useState(false);
	/** Offline, a built lesson or review that would play a recording not stored. */
	const blockedOffline = (steps: LessonStep[]): boolean =>
		offline && !offlineAudio.stored(stepsAudio(steps, course), assetSource);

	useEffect(() => {
		document.documentElement.lang = st.lang;
	}, [st.lang]);

	// Any real activation (re)starts the cue context; the OS owns the volume.
	useEffect(() => armCueUnlock(), []);

	const switchLang = (next: LangId) => {
		if (next === st.lang) return;
		record({ kind: "pref", patch: { lang: next } });
	};

	/**
	 * Opens the gate in place of a round that needs sound. The clock is read in
	 * the tap that got here, never in render.
	 */
	const openGate = useCallback(
		(
			reason: GateReason,
			round: Pick<ActiveLesson, "unitId" | "lessonId" | "mode">,
			open: () => void,
			// A tap that has just turned listening off knows the window; the ref has not seen it yet.
			justPaused = false,
		) => {
			const off = offRef.current;
			const left = justPaused ? LISTENING_OFF_MS / 60_000 : off ? minutesLeft(off, Date.now()) : 1;
			const event = gateEvent(round, reason, left);
			setLessonSheet(null);
			setGate({ reason, minutesLeft: left, event, open });
		},
		[],
	);
	const onGate = (choice: "resumed" | "left") => {
		const g = gate;
		if (!g) return;
		capture("listening_gate_shown", { ...g.event, choice });
		setGate(null);
		if (choice === "resumed") {
			listening.resume();
			g.open();
		}
	};

	const mutedNow = useCallback(() => isListeningOff(offRef.current, Date.now()), []);

	const startLesson = (
		{ u, lesson }: SheetTarget,
		s: GameState = st,
		c = course,
		muted?: boolean,
	) => {
		const quiet = muted ?? mutedNow();
		const built = buildLesson(lesson, u.def, s, c);
		const mutedSteps = quiet ? silenceSteps(built, lessonSilencer(lesson, u.def, c)) : built;
		const round = { unitId: u.id, lessonId: lesson.id, mode: null };
		const reason = quiet ? gateFor({ recipe: c.recipeById(lesson.recipe), mutedSteps }) : null;
		if (reason) {
			openGate(reason, round, () => startLesson({ u, lesson }, s, c, false));
			return;
		}
		// A quiet round plays no recording. The sound round is checked by the
		// lesson itself, not `lessonOffline`: the gate's "turn on" runs this from a
		// render that still saw listening off, and that must not wave it through.
		if (!quiet && lessonLacksSound(u, lesson)) {
			setOfflineNotice(true);
			return;
		}
		capture("lesson_started", {
			unit_id: u.id,
			lesson_id: lesson.id,
			lesson_kind: lesson.kind,
		});
		setLessonSheet(null);
		moved.current = true;
		window.scrollTo(0, 0);
		setLesson({
			...round,
			steps: mutedSteps,
			key: uid(),
			...(canMuteRound({ recipe: c.recipeById(lesson.recipe) })
				? { silence: lessonSilencer(lesson, u.def, c) }
				: {}),
			...(closesRound({ recipe: c.recipeById(lesson.recipe) })
				? { reopen: () => startLesson({ u, lesson }, s, c, false) }
				: {}),
			...(quiet ? { muted: mutedSummary(built, mutedSteps) } : {}),
		});
	};
	const [tour, setTour] = useState(false);
	const [reconciled, setReconciled] = useState(false);
	useEffect(() => {
		if ((!pulled && !firstLesson) || reconciled) return;
		const r = reconcile(device, st);
		if (r.patch) record({ kind: "pref", patch: r.patch });
		if (r.device) onDevice(r.device);
		setTour(r.tour);
		setReconciled(true);
		if (firstLesson) {
			onFirstLesson();
			// Read back after the record above: lesson one is built in the language
			// just chosen on the welcome screen, not the browser's default.
			const fresh = store.getState().state;
			const c = content.course(fresh.lang);
			const u = c.views.units()[0];
			const l = u?.def.lessons[0];
			if (u && l && !Object.keys(fresh.done).length) startLesson({ u, lesson: l }, fresh, c);
		}
	}, [pulled, firstLesson]);

	// Once a week, after the account's log is folded in: what the tree gained
	// since the week before. A week with nothing gained shows nothing.
	useEffect(() => {
		if (!treeMemoryLoaded || !pulled || !reconciled) return;
		const now = Date.now();
		const start = weekStart(now);
		const was = treeMemory.week;
		if (was && was.start >= start) return;
		const tree = treeState(store.getState().state, now);
		if (was && tree.growth > 0 && worthAWeek(was.tree, tree)) {
			setWeek({ from: was.tree, to: tree, since: was.at });
			capture("tree_week_shown", { stage: tree.stage });
		}
		rememberTree({ week: { start, at: now, tree } });
	}, [treeMemoryLoaded, pulled, reconciled]);

	const finishTour = () => {
		record({ kind: "pref", patch: { onboarded: ONBOARDING_VERSION } });
		onDevice({ onboarded: ONBOARDING_VERSION });
		setTour(false);
	};

	const [saveOpen, setSaveOpen] = useState(false);
	// The result the guest dismissed the prompt for; a new result asks again.
	const [dismissedFor, setDismissedFor] = useState<Finished | null>(null);
	const wall = guestWall(guest, st, course.units[1]?.id);
	// The stage-up celebration and the Polaroid moments own the screen until they are done.
	// Moments count only under their result: one closed early must not hold the prompt back.
	const inLesson = lesson !== null || celebrating || (result !== null && momentsLeft > 0);
	// A ref the stale-build reload reads at the moment the tab hides, not at render.
	const inLessonRef = useRef(inLesson);
	useEffect(() => {
		inLessonRef.current = inLesson;
	}, [inLesson]);
	useEffect(() => {
		setBusy(() => inLessonRef.current);
	}, []);
	const prompt = savePrompt({
		wall,
		inLesson,
		resultPending: result !== null && dismissedFor !== result,
		opened: saveOpen,
	});
	useEffect(() => {
		if (prompt !== "hidden") capture("save_prompt_shown", { wall });
	}, [prompt]);
	const dismissSave = () => {
		capture("save_prompt_dismissed");
		setSaveOpen(false);
		if (result) setDismissedFor(result);
	};
	const onSaved = (u: AuthUser, method: SignInMethod) => {
		setSaveOpen(false);
		onLinked(u, method);
	};

	const startPractice = (mode: PracticeMode, muted?: boolean) => {
		const quiet = muted ?? mutedNow();
		const built = buildPractice(mode, st, course);
		// A card can go stale between render and tap (a mistake ageing out of the
		// window), and a lesson with no steps has no first step to render.
		if (!built.length) return;
		const steps = quiet ? silenceSteps(built, reviewSilencer(st, course)) : built;
		const round = { unitId: null, lessonId: null, mode };
		const reason = quiet ? gateFor({ mode, mutedSteps: steps }) : null;
		if (reason) {
			openGate(reason, round, () => startPractice(mode, false));
			return;
		}
		if (!quiet && blockedOffline(steps)) {
			setOfflineNotice(true);
			return;
		}
		capture("review_started", { practice_mode: mode });
		moved.current = true;
		window.scrollTo(0, 0);
		setLesson({
			...round,
			steps,
			key: uid(),
			...(canMuteRound({ mode }) ? { silence: reviewSilencer(st, course) } : {}),
			...(closesRound({ mode }) ? { reopen: () => startPractice(mode, false) } : {}),
			...(quiet ? { muted: mutedSummary(built, steps) } : {}),
		});
	};

	/**
	 * A review of exactly the keys it is given: a unit's due items from Home's
	 * Review badge, the checkpoint's misses from the result screen. It clears
	 * `result` itself, because the result owns the screen this replaces and
	 * leaving it set would put the result back on top of the new lesson.
	 */
	const startKeyReview = (keys: ProgressKey[], muted?: boolean) => {
		const quiet = muted ?? mutedNow();
		const built = buildKeyReview(keys, st, course);
		// Keys no review exercise can quiz build nothing, and a lesson with no
		// steps has no first step to render.
		if (!built.length) return;
		const steps = quiet ? silenceSteps(built, reviewSilencer(st, course)) : built;
		const round = { unitId: null, lessonId: null, mode: null };
		const reason = quiet ? gateFor({ mutedSteps: steps }) : null;
		if (reason) {
			setResult(null);
			openGate(reason, round, () => startKeyReview(keys, false));
			return;
		}
		if (!quiet && blockedOffline(steps)) {
			setOfflineNotice(true);
			return;
		}
		capture("review_started", { key_count: keys.length });
		moved.current = true;
		window.scrollTo(0, 0);
		setResult(null);
		setLesson({
			...round,
			steps,
			key: uid(),
			silence: reviewSilencer(st, course),
			...(quiet ? { muted: mutedSummary(built, steps) } : {}),
		});
	};

	const finish = (fin: LessonDone): void => {
		if (!lesson) return;
		const { done: settled, empty } = settleDone(fin, lesson.muted);
		const { exercises, bestRun, durationMs, ...res } = settled;
		// Silenced down to nothing graded: there is no lesson to record, and nothing to celebrate.
		if (empty) {
			capture("lesson_abandoned", abandonProps(lesson, res.muted));
			tapped.current = undefined;
			setLesson(null);
			if (tab === "learn") requestFocus(true);
			return;
		}
		// One instant for the analytics event and the tree moment.
		const now = Date.now();
		const s = store.getState();
		const e = makeEvent({ kind: "lesson", lessonId: lesson.lessonId, ...res }, s.ctx);
		const before = s.state;
		s.record(e);
		capture("lesson_completed", completionProps(lesson, { ...res, exercises }, now, res.muted));
		tapped.current = undefined;
		setLesson(null);
		// Back to the map's place only when the lesson left from the map.
		if (tab === "learn") requestFocus(true);
		window.scrollTo(0, 0);
		const after = store.getState().state;
		const moment = treeMoment(before, after, now);
		// Read and written through the ref, not a setState updater: the updater
		// runs later, so it could not hand back what this finish should play.
		// Before this learner's memory has loaded, a finish takes no photo:
		// writing then would replace the stored dates. The load resolves at
		// startup, long before a lesson ends; the stop is still caught up
		// silently once it does.
		let play: string[] = [];
		if (loadedForRef.current === userId) {
			const r = atFinish(
				polaroidsRef.current,
				before,
				after,
				stops,
				takenAt(now, moment.to.season),
			);
			play = r.play;
			// Writes the ref before it returns, so a second finish right after reads this one's claim.
			rememberPolaroids(r.memory);
		}
		const summary = summarise(before, after, e);
		setEndStep("lesson");
		setResult({
			summary,
			moment,
			polaroids: play,
			lessonStep: lessonStep(summary, { bestRun, durationMs }, before, after),
			treeNeeded: treeStepNeeded(resultModel(summary, moment), moment, moment.stageUp),
			tag: { unitId: lesson.unitId, lessonId: lesson.lessonId, mode: lesson.mode },
		});
		setMomentsLeft(play.length);
		setCelebrating(moment.stageUp);
		if (moment.stageUp) capture("tree_stage_up", { stage: moment.to.stage });
		// The result shows this tree full size: the header has nothing new to announce about it.
		rememberTree({ seen: { tree: moment.to, opened: treeMemory.seen?.opened ?? false } });
	};

	const onPaused = (round: ActiveLesson, p: ListeningPause): void => {
		tapped.current = { swapped: p.swapped, dropped: p.dropped, skipped: 1 };
		capture("listening_paused", pausedProps(round, p));
	};

	/** "Can't listen now" in a round that needs sound: it closes, unrecorded, onto the gate. */
	const onClosed = (round: ActiveLesson, reopen: () => void, p: ListeningPause): void => {
		capture("listening_paused", pausedProps(round, p, true));
		capture("lesson_abandoned", abandonProps(round, round.muted));
		tapped.current = undefined;
		setLesson(null);
		openGate(round.mode === "listen" ? "listen" : "letters", round, reopen, true);
	};

	const onWater = (from: WaterFrom) => {
		const keys = waterRound(st, Date.now());
		capture("tree_watered", { from, key_count: keys.length });
		startKeyReview(keys);
	};
	const onRenameTree = (name: string) => {
		if (name !== st.treeName) record({ kind: "pref", patch: { treeName: name } });
	};
	const onShareTree = (scene: HTMLElement) => {
		const tree = treeState(st, Date.now());
		void shareTree(scene, shareText({ name: st.treeName, tree, t: translator(st.lang) })).then(
			(outcome) => {
				capture("tree_shared", { outcome, stage: tree.stage });
			},
		);
	};

	/**
	 * Leaving mid-lesson. It reports an abandonment and drops the lesson —
	 * deliberately NOT `finish`, which would record a `lesson` event with no
	 * steps and capture a completion for a lesson the learner walked away from.
	 */
	const abandon = (): void => {
		if (!lesson) return;
		capture("lesson_abandoned", abandonProps(lesson, lesson.muted ?? tapped.current));
		tapped.current = undefined;
		setLesson(null);
		// Back to the map's place only when the lesson left from the map.
		if (tab === "learn") requestFocus(true);
	};

	// Callbacks for page components
	const onLesson = (u: UnitView, l: LessonDef) => setLessonSheet({ u, lesson: l });
	const onPick = (l: LetterView) => setLetterSheet(l);
	const onContinue = () => {
		setResult(null);
		setCelebrating(false);
		setArrive((n) => n + 1);
	};
	const onCloseLessonSheet = () => setLessonSheet(null);
	const onStartLesson = () => {
		if (lessonSheet) startLesson(lessonSheet);
	};
	const onCloseLetterSheet = () => setLetterSheet(null);
	const onSharePolaroid = (id: string, el: HTMLElement) => {
		const stop = stops.find((s) => s.id === id);
		if (!stop) return;
		const t = translator(st.lang);
		void sharePolaroid(el, {
			hy: stop.place.hy,
			name: stop.place.name,
			date: photoDate(polaroidsRef.current?.taken[id] ?? null, st.lang),
			line: t("polaroid.shareLine"),
			text: t("polaroid.shareText", { name: stop.place.name }),
		}).then((outcome) => {
			capture("polaroid_shared", { place: id, from: "moment", outcome });
		});
	};
	const onOpenTree = () => {
		const tree = treeState(st, Date.now());
		capture("home_sheet_opened", {
			season: tree.season,
			stage: tree.stage,
			// fresh: the sheet has something new to show: never opened, or a photo pinned since.
			fresh: !treeMemory.seen?.opened,
		});
		const seen = treeMemory.seen?.tree;
		setTreeFrom(seen && gained(seen, tree) ? seen : undefined);
		rememberTree({ seen: { tree, opened: true } });
		setTreeOpen(true);
	};
	const onCloseTree = () => setTreeOpen(false);
	const onOpenPlace = (id: string) => {
		const stop = stops.find((s) => s.id === id);
		if (!stop) return;
		setPlaceSheet(id);
		const state = stopState(st, stop);
		// A finished stop's sheet shows its photo, as Home does: the window has nothing new
		// left to announce. A locked or current one shows none, so the pin's sparkle stays.
		const seen = treeMemory.seen;
		if (state === "done" && !seen?.opened)
			rememberTree({ seen: { tree: seen?.tree ?? treeState(st, Date.now()), opened: true } });
		capture("place_opened", { place: id, state });
	};

	const ctxValue = useMemo<AppLayoutContextValue>(
		() => ({
			st,
			guest,
			openSave: () => setSaveOpen(true),
			onLeaveGuest,
			sync,
			tab,
			setTab,
			lessonSheet,
			setLessonSheet,
			letterSheet,
			setLetterSheet,
			lesson,
			setLesson,
			result,
			setResult,
			treeOpen,
			onOpenTree,
			onCloseTree,
			seed,
			treeMemory,
			treeFrom,
			arrive,
			onWater,
			onRenameTree,
			onShareTree,
			polaroids,
			polaroidsLoaded,
			polaroidsRef,
			rememberPolaroids,
			photo,
			openPhoto: setPhoto,
			closePhoto: () => setPhoto(null),
			revealing,
			onOpenPlace,
			placeSheet,
			setPlaceSheet,
			stops,
			assetSource,
			player,
			record,
			switchLang,
			startLesson,
			lessonOffline,
			startPractice,
			onReviewKeys: startKeyReview,
			finish,
			focus: pendingFocus,
			onFocused: () => {
				if (pendingFocus) setFocusHandled(pendingFocus.n);
			},
			// Callbacks for page components
			onLesson,
			onPick,
			onContinue,
			onCloseLessonSheet,
			onStartLesson,
			onCloseLetterSheet,
			email,
			onSignOut,
			onDelete,
		}),
		[
			st,
			guest,
			onLeaveGuest,
			sync,
			tab,
			setTab,
			lessonSheet,
			setLessonSheet,
			letterSheet,
			setLetterSheet,
			lesson,
			setLesson,
			result,
			setResult,
			treeOpen,
			onOpenTree,
			onCloseTree,
			seed,
			treeMemory,
			treeFrom,
			arrive,
			onWater,
			onRenameTree,
			onShareTree,
			polaroids,
			polaroidsLoaded,
			rememberPolaroids,
			photo,
			revealing,
			onOpenPlace,
			placeSheet,
			setPlaceSheet,
			stops,
			assetSource,
			player,
			record,
			switchLang,
			startLesson,
			lessonOffline,
			startPractice,
			startKeyReview,
			finish,
			pendingFocus,
			onLesson,
			onPick,
			onContinue,
			onCloseLessonSheet,
			onStartLesson,
			onCloseLetterSheet,
			email,
			onSignOut,
			onDelete,
		],
	);

	// The three screens: a result first, then an active lesson, then the routed
	// page. `finish` clears `lesson` itself, so a result and its lesson are never
	// both set, and the result therefore wins on its own. Both replace the
	// routed page, exactly as the pre-router `App.tsx` did. The sheets belong to
	// the map screen only: during a lesson there is no map to put them over.
	const body = () => {
		// A new guest is on their way into lesson one; the map would only flash.
		if (firstLesson && !reconciled) {
			return (
				<Shell>
					<Splash />
				</Shell>
			);
		}

		if (result && endStep === "lesson") {
			return (
				<Shell>
					<LessonDoneStep
						step={result.lessonStep}
						onShown={() => {
							capture("result_step_shown", {
								step: "lesson",
								best_run: result.lessonStep.bestRun ?? undefined,
								perfect: result.lessonStep.perfect,
							});
							if (result.lessonStep.perfectLine)
								capture("moment_shown", momentProps(result.tag, { kind: "perfect" }));
						}}
						onContinue={() => {
							if (result.treeNeeded) setEndStep("tree");
							else if (result.polaroids.length > 0) setEndStep("moments");
							else onContinue();
						}}
						onReviewKeys={startKeyReview}
					/>
				</Shell>
			);
		}

		if (result) {
			return (
				<Shell>
					<Result
						treeStep={endStep === "tree"}
						onShown={() => capture("result_step_shown", { step: "tree" })}
						res={result.summary}
						moment={result.moment}
						seed={seed}
						lang={st.lang}
						polaroids={result.polaroids.flatMap((id) => {
							const stop = stops.find((s) => s.id === id);
							return stop ? [{ stop, taken: polaroidsRef.current?.taken[id] ?? null }] : [];
						})}
						onPolaroidShutter={(id) => {
							capture("polaroid_awarded", { place: id });
						}}
						onPolaroidShare={onSharePolaroid}
						onPinned={() => {
							setMomentsLeft((n) => Math.max(0, n - 1));
							// The header window sparkles until Home (onOpenTree) or a finished
							// stop's sheet (onOpenPlace) is opened.
							rememberTree({ seen: { tree: result.moment.to, opened: false } });
						}}
						celebrating={celebrating}
						onCelebrated={() => setCelebrating(false)}
						onContinue={onContinue}
						onReviewKeys={startKeyReview}
						onWater={result.moment.toSummer > 0 ? () => onWater("result") : undefined}
					/>
				</Shell>
			);
		}

		if (gate) {
			return (
				<Shell>
					<Gate
						minutesLeft={gate.minutesLeft}
						onTurnOn={() => onGate("resumed")}
						onBack={() => onGate("left")}
					/>
				</Shell>
			);
		}

		if (lesson) {
			const reopen = lesson.reopen;
			return (
				<Shell>
					{/* key remounts per lesson, so a second lesson starts on step one
              rather than inheriting the last one's queue. */}
					<Lesson
						key={lesson.key}
						initial={lesson.steps}
						course={course}
						onQuit={abandon}
						onDone={finish}
						onMiss={(m) => {
							const p = missProps(lesson, m, course, content.contentVersion);
							if (p) capture("answer_missed", p);
						}}
						composedBefore={hasComposed(st.stats)}
						silence={lesson.silence}
						muted={lesson.muted !== undefined}
						onPaused={(p) => onPaused(lesson, p)}
						{...(reopen ? { onClose: (p: ListeningPause) => onClosed(lesson, reopen, p) } : {})}
						onMoment={(m) => capture("moment_shown", momentProps(lesson, m))}
					/>
				</Shell>
			);
		}

		return (
			<>
				<Outlet />
				<Overlays
					onPlaceUnit={(unitId) => requestFocus(true, unitId)}
					week={
						week &&
						!tour && (
							<WeekSheet
								from={week.from}
								to={week.to}
								since={week.since}
								lang={st.lang}
								seed={seed}
								onClose={() => setWeek(null)}
								onShare={onShareTree}
							/>
						)
					}
				/>
			</>
		);
	};

	return (
		<StringsProvider lang={st.lang}>
			<SoundProvider source={assetSource} player={player}>
				<ListeningScope>
					<AppLayoutContext.Provider value={ctxValue}>
						{/* Above every screen AND the overlay group, which is where the
              sheets live. Scoping this to each page left the sheets — siblings
              of the Outlet, above every page — reading a null course. */}
						<CourseProvider course={course}>
							<WritingProvider
								layout={st.layout}
								hinted={st.kbdHinted}
								known={knownLetters}
								onHinted={onHinted}
								store={writingStore}
							>
								{body()}
							</WritingProvider>
							{tour && !lesson && !result && (
								<Onboarding mode="tour" lang={st.lang} onLang={switchLang} onFinish={finishTour} />
							)}
							{prompt !== "hidden" && (
								<SaveProgressSheet
									auth={auth}
									email={email}
									dismissible={prompt === "dismissible"}
									onDismiss={dismissSave}
									onLinked={onSaved}
									onLeaveGuest={onLeaveGuest}
									onSaving={onSaving}
								/>
							)}
							{offlineNotice && <OfflineNotice onClose={() => setOfflineNotice(false)} />}
						</CourseProvider>
					</AppLayoutContext.Provider>
				</ListeningScope>
			</SoundProvider>
		</StringsProvider>
	);
}
